// Admin-facing copy for the amenity Payments page (per-site Stripe Connect setup + the
// read-only owed balance).
//
// A running theme in here: several of these strings exist to say plainly that something does NOT
// work yet. The three setup paths are backed by config that is only partly provisioned, and the
// owed balance is computed from a ledger with two known gaps. Copy that papered over either would
// hand an admin a button that throws, or a number someone pays against twice.

export default {
    title: 'Payments',
    // Sits under the page title.
    subtitle: 'Choose how each property gets paid for amenity bookings.',
    pageTitle: 'Amenity payments',

    // ===== list =====
    // Field label on each card's status line (the page is a card list, not a table).
    modeLabel: 'How you get paid',
    noSites: 'No properties yet. Add a property before setting up payments.',
    loadError: 'Could not load payment settings.',
    // Shown in a row whose own connect-status call failed — the rest of the table still renders.
    rowLoadError: 'Could not load this property’s payment settings.',
    retry: 'Try again',
    notEnabled: 'Amenity booking is not enabled for this account. Contact Swiftlane to enable it.',

    // ===== status labels =====
    statusNotSetUp: 'Not set up',
    statusOnboardingIncomplete: 'Onboarding incomplete',
    statusActive: 'Active',
    statusDisconnected: 'Disconnected',

    // ===== how-you-get-paid labels =====
    modeDirect: 'Straight to your Stripe account',
    modePlatform: 'Swiftlane collects and pays you',
    modeUnset: 'Not set up',
    kindOauth: 'Your own Stripe account',
    kindExpress: 'Stripe account set up through Swiftlane',
    kindNone: 'No Stripe account',
    // TRANSFER_SPEC §D8 — a transfers-only account: Swiftlane pays the balance into it by Stripe
    // transfer; it never takes charges.
    kindRecipient: 'Bank transfers via Stripe',

    // Stated at the top of the page rather than discovered one failed click at a time. Of the three
    // setup paths, only "let Swiftlane collect" works end to end today: OAuth needs a Connect
    // platform client id nobody has provisioned, and Express needs Connect enabled on Swiftlane's
    // own Stripe account. Deliberately says what an admin can DO about it, and does not mention the
    // third known gap (the amenity webhook secret missing from the production values file, which
    // would leave payments unconfirmed) — that one is a deployment concern no admin can act on, and
    // a permanent unactionable warning here would just be noise. It is raised with the backend team
    // instead.
    prerequisitesNotice:
        'Right now, only “Let Swiftlane collect” is ready to use. Connecting or creating a Stripe account needs setup on Swiftlane’s side that isn’t finished yet — contact Swiftlane if you need one of those.',

    // ===== the three-way chooser (unconfigured site) =====
    chooserHeading: 'How should this property get paid?',
    chooserHint: 'You can change this later.',

    oauthCardTitle: 'Connect your Stripe account',
    oauthCardBody: 'Already use Stripe? Link it and payments go straight to you.',
    oauthCardAction: 'Connect Stripe',
    // The OAuth path calls Stripe's hosted consent screen, which needs a Connect platform client id
    // Swiftlane has not provisioned yet — the backend raises ConnectConfigurationError and returns
    // 503 for every attempt. The card stays visible so the option is discoverable, but the button
    // is disabled and says why rather than throwing on click.
    oauthUnavailableTag: 'Not available yet',
    oauthUnavailableReason: 'Linking an existing Stripe account needs Stripe Connect enabled on Swiftlane’s side, which isn’t done yet.',

    expressCardTitle: 'Create a Stripe account',
    expressCardBody: 'We’ll help you set one up. Takes a few minutes.',
    expressCardAction: 'Set up Stripe',

    platformCardTitle: 'Let Swiftlane collect',
    platformCardBody: 'We collect payments and pay you separately. No Stripe account needed.',
    // Not a repeat of the card title above — a card whose button restates its own heading gives a
    // screen-reader user the same phrase twice with no added meaning, and reads as a template gap.
    platformCardAction: 'Choose this',

    // ===== confirms =====
    // Collect mode is snapshotted onto each payment as it is taken, so a change is never
    // retroactive. Saying "from now on" is the whole point of this dialog.
    platformConfirmTitle: 'Let Swiftlane collect payments?',
    platformConfirmBody:
        'New bookings from now on will be collected by Swiftlane and paid out to you separately. Bookings already paid for keep the arrangement they were made under — this does not change them.',
    platformConfirmOk: 'Yes, Swiftlane collects',

    directConfirmTitle: 'Send payments straight to your Stripe account?',
    directConfirmBody:
        'New bookings from now on will be charged directly to your connected Stripe account. Bookings already paid for keep the arrangement they were made under — this does not change them.',
    directConfirmOk: 'Yes, pay me directly',

    cancel: 'Cancel',
    // Confirmations of what actually changed — deliberately repeating "from now on", since the
    // whole risk with this setting is someone assuming it applied backwards.
    platformSetMsg: 'From now on, Swiftlane collects payments for this property.',
    directSetMsg: 'From now on, payments for this property go straight to your Stripe account.',

    // ===== per-status actions =====
    switchToPlatform: 'Switch to Swiftlane collecting',
    switchToDirect: 'Switch to direct payouts',
    finishOnboarding: 'Finish Stripe setup',
    onboardingIncompleteHint:
        'Stripe hasn’t finished checking this account yet, so bookings can’t be charged. Finish setup to start taking payments.',
    disconnectedHint:
        'Swiftlane’s access to this Stripe account was revoked, so bookings can’t be charged. Reconnect to start taking payments again.',
    // Shown on a PLATFORM site whose Stripe link was separately revoked. Payments still work,
    // because they run on Swiftlane's account — but the revocation is real and should not be
    // silently hidden just because it is currently harmless.
    revokedButPlatformHint:
        'Swiftlane’s access to this property’s Stripe account was revoked. Payments still work, because Swiftlane collects them.',
    // The state a disconnect LEAVES a Swiftlane-collects property in, which the hint above cannot
    // describe: there is no Stripe account here and there never was one, so "access was revoked"
    // would be a plain falsehood printed directly under the words "No Stripe account".
    // Both halves matter — setup is reopened, and residents are still being charged in the
    // meantime. Saying only the first would repeat the exact misreading the disconnect confirm
    // exists to prevent.
    disconnectedButPlatformHint:
        'This property isn’t set up with a Stripe account. Residents are still being charged — Swiftlane collects for this property, and disconnecting doesn’t stop that. Choose how it should get paid below.',

    // A disconnect endpoint exists as of BE PR #3086, so this is no longer the Stripe-dashboard
    // workaround it used to be — it explains what the button beside it does. The Stripe-side step
    // is still named, because disconnecting here genuinely does NOT revoke the authorization on
    // Stripe's side; the portal just stops using it.
    switchAccountHeading: 'Want to use a different Stripe account?',
    switchAccountBody:
        'Disconnect this one first, then choose how this property should get paid. Disconnecting doesn’t revoke anything in Stripe — to do that as well, remove the Swiftlane application from the property’s own Stripe dashboard (Settings → Connected applications).',
    // Was a genuine dead end until BE PR #3086: a kind-NONE row could never link a real Stripe
    // account from any API (connect-link refused it with ALREADY_LINKED, and collect-mode refuses
    // CONNECT while there is no account). One disconnect write is what opens it, so this points at
    // the button now instead of at support.
    platformLockedHeading: 'Want your own Stripe account instead?',
    platformLockedBody:
        'Disconnect first, then choose “Connect your Stripe account” or “Create a Stripe account”. Bookings already paid for aren’t affected.',

    // ===== disconnect =====
    disconnectAction: 'Disconnect',
    disconnectConfirmTitle: 'Disconnect this Stripe setup?',
    // The three facts a confirm has to carry, each mirrored from what the 200 response will say
    // (paymentControls.disconnectFacts). Stated BEFORE the click, so nothing in the result is a
    // surprise.
    disconnectConfirmSnapshot:
        'Bookings that have already been charged are unaffected — refunds for them still go the way they were originally charged.',
    // CONNECT: disconnecting really does stop the charging.
    disconnectConfirmBlocksCharges: 'New bookings won’t be charged until a Stripe account is linked again.',
    // ⚠ PLATFORM: it does NOT. payment_gate's PLATFORM branch never reads deauthorized_at, because
    // the charge lands on Swiftlane's own account. Saying "disconnected" without saying this would
    // let an admin believe they had switched charging off while residents kept being billed. The
    // two real levers are named, because a warning without a remedy is just alarming.
    disconnectConfirmStillCharging:
        'Residents will still be charged for new bookings. Swiftlane collects for this property through its own Stripe account, so disconnecting doesn’t stop that. To stop charging, remove the amenity’s price or turn amenity payments off for this account.',
    disconnectConfirmStripeStays: 'Stripe isn’t called. The connection stays authorized on Stripe’s side until it’s removed there too.',
    disconnectConfirmExpressStays:
        'Stripe isn’t called. The Stripe account Swiftlane created for this property still exists and is unchanged.',
    disconnectConfirmOk: 'Yes, disconnect',
    disconnectedMsg: 'Disconnected. Choose how this property should get paid.',
    // Shown when the row was already disconnected before this click (a retry, or a second tab).
    // The write is unconditional so it succeeded either way — but claiming a change that had
    // already happened would be misleading.
    disconnectedAlreadyMsg: 'This property was already disconnected.',
    // The 200's own `notes`, rendered verbatim under the row. They are composed deterministically
    // by the backend and encode product policy, so they are never re-worded here.
    disconnectResultTitle: 'Disconnected',
    disconnectFailed: 'Could not disconnect this property.',

    // ===== reconcile =====
    reconcileSiteAction: 'Check payments',
    reconcileAllAction: 'Check all payments',
    reconcileTitle: 'Payment check',
    // A company-wide run does up to 200 sequential Stripe reads inside the request, so it can
    // genuinely take a while. Saying so beats a spinner an admin assumes has hung.
    reconcileRunning: 'Checking payments against Stripe. This can take a minute.',
    reconcileNothingToDo: 'All payments are up to date — nothing needed fixing.',
    reconcileConsidered: (n: number): string => `Checked ${n} ${n === 1 ? 'booking' : 'bookings'}.`,
    reconcileRecovered: 'Recovered',
    reconcileRefunded: 'Refunded',
    reconcileClosedOut: 'Payment never completed',
    reconcileLeftInFlight: 'Still in progress',
    reconcileMismatched: 'Needs manual review',
    reconcileSkipped: 'Already up to date',
    reconcileSkippedPaymentsDisabled: 'Payments turned off',
    reconcileErrors: 'Errors',
    reconcileFailed: 'Could not check payments right now. It’s safe to try again.',

    // ===== record remittance =====
    // Now buildable: the backend's over-remittance refusal returns renderable numbers rather than
    // prose with an internal site_id in it, and refunds/disputes do net out of the balance
    // (verified). The Stripe check-before-paying advice stays — see owedCaveat.
    recordRemittance: 'Record a payout',
    remittanceModalTitle: 'Record a payout',
    remittanceAmountLabel: 'Amount paid',
    remittanceAmountPlaceholder: '0.00',
    remittanceNoteLabel: 'Note (optional)',
    remittanceNotePlaceholder: 'Reference, transfer id, anything worth remembering',
    remittanceOwedLine: (amount: string): string => `Currently owed: ${amount}`,
    remittancePeriodLine: (from: string, to: string): string => `Recorded against activity from ${from} to ${to}.`,
    remittanceSubmit: 'Record payout',
    // Deliberately not a success adjective — this records that money moved elsewhere; it does not
    // move any.
    remittanceRecorded: (amount: string): string => `Recorded a ${amount} payout.`,
    remittanceFailed: 'Could not record this payout.',
    remittanceRequired: 'Enter the amount that was paid.',
    remittanceInvalid: 'Enter an amount in dollars and cents, for example 125.00.',
    remittanceNotPositive: 'Enter an amount greater than zero.',
    remittanceOverOwed: (owed: string): string => `That’s more than this property is currently owed (${owed}).`,
    // The server's own 400, formatted from the two integer fields it now returns. Shown even
    // though the form pre-checks, because the balance is recomputed under a lock at submit time
    // and can legitimately have moved.
    remittanceOverServer: (requested: string, owed: string): string =>
        `${requested} is more than this property is currently owed (${owed}). The balance may have changed since this form was opened.`,
    remittanceNoteTooLong: 'Keep the note to 256 characters or fewer.',

    // ===== owed balance =====
    owedTitle: 'Owed to this property',
    owedAllTimeNote: 'All time, less any payouts already recorded.',
    owedWindowHeading: (from: string, to: string): string => `Activity ${from} to ${to}`,
    owedGross: 'Collected',
    owedRefunded: 'Refunded',
    owedPlatformFee: 'Swiftlane fee',
    owedClerkage: 'Cancellation fees kept',
    owedLoadError: 'Could not load the owed balance.',
    // Softened, and only as far as the code actually earns. BOTH of the gaps that made this
    // read-only are now closed and verified in the backend: `charge.refunded` records an
    // externally-issued (Stripe-dashboard) refund, `charge.dispute.created` records a chargeback,
    // and `remittance._net_by_booking` subtracts refunds, clerkage AND disputes from the owed pool
    // — a dispute "removes money from our balance exactly as a refund does, so it MUST net out",
    // in that module's own words. Hence the write action exists at all now.
    //
    // What is NOT claimed, because it isn't true: an orphaned capture still counts as remittable
    // until it is reconciled (hence the pointer to the payment check, which is on this same card),
    // and there is no `charge.dispute.closed` handler — so a dispute Swiftlane later WINS stays
    // subtracted until an operator corrects it, meaning the figure can read low as well as high.
    // Both directions are why the Stripe check survives the softening.
    owedCaveat:
        'Refunds and disputes handled in Stripe are included here. Two things still aren’t: bookings whose payment hasn’t been reconciled (run a payment check first), and disputes Swiftlane later won, which stay deducted until corrected. Check against Stripe before a large payout.',

    // ===== errors =====
    // The backend surfaces Stripe's own refusal verbatim (502 STRIPE_ERROR) — most likely "Connect
    // is not enabled on this platform account". Shown as-is rather than swallowed into a generic
    // failure, because the real text is what tells whoever reads it what to go and fix.
    stripeSetupFailedTitle: 'Stripe couldn’t set that up',
    stripeSetupFailedFallback: 'Stripe refused the request. Contact Swiftlane support if this keeps happening.',
    oauthNotConfiguredError:
        'Linking an existing Stripe account isn’t available yet — Stripe Connect hasn’t been enabled on Swiftlane’s side.',
    collectModeFailed: 'Could not change how this property gets paid.',

    // ===== compact table (payments page redesign) =====
    // Column headers. "How you get paid" reuses modeLabel above.
    columnProperty: 'Property',
    columnStatus: 'Status',
    columnOwed: 'Owed',
    columnActions: 'Actions',
    // The Status cell for a row whose own connect-status call failed. deriveSetupStatus(null)
    // would read "Not set up", which we cannot honestly claim about a row we could not read.
    statusLoadFailed: 'Couldn’t load',
    // The Owed column when the owed call itself failed; the detail panel carries the real error.
    owedUnavailable: 'Unavailable',
    // Owed simply does not apply outside the Swiftlane-collects tier.
    owedNotApplicable: '—',
    // The payment gate's documented fail-open-to-free behaviour: a CONNECT-mode site whose
    // account cannot charge yet (charges_enabled false, link not revoked) lets residents book
    // PAID amenities without paying. "Onboarding incomplete" alone hides that money consequence,
    // so the state gets its own loud tag + a plain-words explanation.
    freeBookingRiskTag: 'Not charging',
    freeBookingRiskWarning:
        'Residents can book paid amenities right now without being charged — until Stripe setup is finished, paid bookings at this property go through for free.',

    // ===== reworked detail panel (2026-09-02) — append-only =====
    // The row's one affordance for un-set-up/disconnected properties; it opens the detail panel,
    // where the three-way choice lives as full option cards.
    setUpAction: 'Set up',
    // Onboarding-incomplete and free-booking-risk are the same underlying fact (a CONNECT account
    // that cannot charge yet); the panel says it ONCE, money consequence included.
    onboardingNotChargingAlert:
        'Stripe hasn’t finished checking this account, so bookings can’t be charged — residents can currently book paid amenities for free. Finish setup to start taking payments.',
    // Plain-words disconnect summary (composed from the response's own booleans); the backend's
    // verbatim policy notes live behind the collapse below, never as the primary copy.
    disconnectDoneSafe: 'Money already collected is safe, and refunds for past bookings keep working exactly as before.',
    disconnectDoneChargesBlocked: 'New bookings won’t be charged until a Stripe account is connected again.',
    disconnectDoneStillCharging:
        'Residents are still being charged for new bookings — Swiftlane collects for this property, and disconnecting doesn’t change that.',
    disconnectDetailsLabel: 'What does this mean?',
    // The facts strip.
    factAccount: 'Stripe account',
    factHowLinked: 'Stripe link',
    factConnectedOn: 'Connected',
    factRevokedOn: 'Revoked',

    // ===== Command Center cross-company tracking view (read-only) =====
    ccNavTitle: 'Amenity Payments',
    ccTitle: 'Amenity payments',
    ccSubtitle:
        'Every property’s amenity payment setup, across all accounts. Read-only — changes are made from each account’s own Payments page.',
    ccColumnCompany: 'Account',
    ccColumnSite: 'Property',
    ccColumnConnected: 'Connected',
    ccColumnRevoked: 'Revoked',
    ccLoadError: 'Could not load the payment links.',
    ccEmpty: 'No properties have amenity payment setup yet.',

    // ===== Express return / refresh landing pages =====
    returnDoneTitle: 'Stripe setup complete',
    returnDoneBody: 'This property can now take amenity booking payments.',
    returnPendingTitle: 'Stripe is still reviewing this account',
    returnPendingBody:
        'Stripe hasn’t finished checking the details yet. This can take a few minutes. You can close this page — the status updates on its own.',
    returnMissingSite: 'We couldn’t tell which property this was for. Open Payments to check its status.',

    // ===== OAuth connect-callback errors (backend redirects here with ?connect_error=CODE) =====
    connectErrorDeniedTitle: 'Stripe connection was declined',
    connectErrorDeniedBody:
        'The Stripe account wasn’t connected — the request was declined on Stripe’s page. Nothing changed for this property; you can try again any time.',
    connectErrorExpiredTitle: 'That connection link expired',
    connectErrorExpiredBody:
        'Each connection link works once and only for a short while. Go back to Payments and click Connect Stripe to get a fresh one.',
    connectErrorAlreadyLinkedTitle: 'This property already has a Stripe setup',
    connectErrorAlreadyLinkedBody: 'Disconnect the current Stripe setup first, then connect the account you want instead.',
    connectErrorAccountTakenTitle: 'That Stripe account is already in use',
    connectErrorAccountTakenBody: 'Another property is already connected to that Stripe account. Each property needs its own.',
    connectErrorGenericTitle: 'Stripe connection didn’t complete',
    connectErrorGenericBody:
        'Something went wrong finishing the connection. Nothing changed for this property — go back to Payments and try again.',

    // ===== expanded-panel round-2 polish (live review) =====
    switchAccountCollapseLabel: 'Want to use a different Stripe account?',
    refreshTitle: 'That setup link expired',
    refreshBody: 'Stripe onboarding links are short-lived. Start again to get a fresh one.',
    refreshAction: 'Start again',
    backToPayments: 'Back to Payments',

    // ===== two-tab redesign (2026-09-03) — append-only =====
    // Property-admin voice throughout: THEIR properties, THEIR money. This page carries no
    // Swiftlane-side controls — the admin is the recipient, not the payer.
    pageSubtitle:
        'Choose how each of your properties collects money for amenity bookings — and track the money Swiftlane is holding and paying out to you.',

    // Tab labels.
    tabSetup: 'Payment setup',
    tabMoney: 'Accounts & money',

    // ----- Payment setup tab: summary strip -----
    summaryProperties: 'Properties',
    summaryCollecting: 'Collecting now',
    summaryNeedsSetup: 'Needs setup',
    summarySwiftlaneCollects: 'Swiftlane collects',
    summaryDirect: 'Direct payout',

    // ----- Payment setup tab: "How amenity payments work" explainer (collapsed by default) -----
    // Replaces the old wall of always-on alerts: the two ways money can flow, one click away.
    explainerHeading: 'How amenity payments work',
    explainerDirectTitle: 'Direct payout',
    explainerDirectBody:
        'Payments go straight to your property’s own Stripe account. The money lands in your account and Swiftlane never holds it. Needs a one-time Stripe onboarding.',
    explainerPlatformTitle: 'Swiftlane collects',
    explainerPlatformBody:
        'Swiftlane collects the payments and pays you separately. No Stripe account and no onboarding — one click and the property is set up.',

    // ----- Bulk setup ("Set up all my properties") -----
    bulkAction: 'Set up all my properties',
    bulkModalTitle: 'Set up all your properties?',
    bulkModalBody:
        'This enrols every property that isn’t set up yet into “Swiftlane collects” — zero onboarding. Properties that are already set up are left exactly as they are. You can switch any property to direct payout later.',
    bulkModalConfirm: 'Yes, set them all up',
    bulkRunning: 'Setting up your properties…',
    bulkResultTitle: 'Setup results',
    // Group headings in the results view.
    bulkEnrolledHeading: (n: number): string => `Set up (${n})`,
    bulkSkippedHeading: (n: number): string => `Already set up (${n})`,
    bulkFailedHeading: (n: number): string => `Couldn’t set up (${n})`,
    // The honest all-skipped outcome — nothing needed doing.
    bulkNoneToEnrol: 'Every property was already set up — nothing needed changing.',
    // The toast summary after a run.
    bulkDoneToast: (enrolled: number): string =>
        enrolled === 1 ? 'Set up 1 property to let Swiftlane collect.' : `Set up ${enrolled} properties to let Swiftlane collect.`,
    bulkFailed: 'Could not set up your properties right now. It’s safe to try again.',
    bulkClose: 'Done',

    // ----- Bulk setup: the three-option chooser (2026-09-03) -----
    // Clicking "Set up all my properties" now opens a three-way choice, each applied to EVERY
    // property at once. Mirrors the per-site SetupChoiceCards look/copy, but every option here is
    // company-wide: Swiftlane-collects is instant for all; both Stripe options use ONE shared
    // account across all properties and take the admin to Stripe to finish once.
    bulkChooserTitle: 'Set up all your properties',
    bulkChooserHint: 'Pick one way to set up every property at once. You can still change any property individually later.',
    // The two Stripe options both use a single shared account — stated once, above the cards, so it
    // is not a surprise buried in a card body.
    bulkStripeSharedNote: 'Both Stripe options use one account across all your properties, and take you to Stripe to finish once.',

    // Card 1 — Swiftlane collects (instant, unchanged behaviour: enrol-all then show results).
    bulkPlatformCardTitle: 'Connect with Swiftlane',
    bulkPlatformCardBody:
        'Swiftlane collects payments and pays you separately. Instant — no Stripe account and no onboarding. Sets up every property that isn’t set up yet.',
    bulkPlatformCardAction: 'Set them all up',

    // Card 2 — create one Express account for all properties, then redirect to Stripe onboarding.
    bulkExpressCardTitle: 'Create a Stripe account',
    bulkExpressCardBody: 'Create one Stripe account for all your properties. We’ll take you to Stripe to finish onboarding once.',
    bulkExpressCardAction: 'Create Stripe account',

    // Card 3 — connect one existing Stripe account (OAuth) to all properties, then redirect.
    bulkOauthCardTitle: 'Connect your existing Stripe',
    bulkOauthCardBody:
        'Already use Stripe? Connect one account you already have to all your properties. We’ll take you to Stripe to authorize once.',
    bulkOauthCardAction: 'Connect Stripe',

    // A connect-link (redirect) call that failed before it could hand off to Stripe.
    bulkStripeLinkFailed: 'Could not start Stripe setup right now. It’s safe to try again.',

    // ----- Accounts & money tab: KPI band -----
    // "Your share", not "Collected …": this tile is the NET (it equals the fee breakdown's "Your
    // share" subtotal directly beneath it), while the breakdown opens with "Collected from
    // residents" — the GROSS. One word for one number. Its "This month" line is the period property
    // share too (backend `collected_this_month_cents` is the windowed net, never the gross).
    kpiCollectedTitle: 'Your share',
    kpiCollectedAllTime: 'All time',
    kpiCollectedThisMonth: 'This month',
    kpiOwedTitle: 'Owed to you',
    kpiOwedSub: (n: number): string => `Pending across ${n} ${n === 1 ? 'property' : 'properties'}`,
    kpiOwedSubNone: 'Nothing pending',
    kpiPaidTitle: 'Paid to you',
    kpiPaidToDate: 'Received to date',
    kpiPaidLast: (date: string): string => `Last payment ${date}`,
    kpiPaidLastNone: 'No payments received yet',

    // Amounts on this tab are the property's share; say so once, plainly.
    moneyShareNote: 'Amounts shown are your share, after the platform fee.',

    // ----- Accounts & money tab: fee breakdown (real Stripe fees, 2026-09) -----
    // The waterfall that reconciles the headline: every line is one term of the backend's own
    // identity (gross - refunds - chargebacks - card processing - chargeback fees - Swiftlane fee
    // - cancellation fees kept = your share; your share - paid = owed), so it adds up on screen.
    breakdownTitle: 'How your balance is worked out',
    breakdownAllTimeNote: 'All time',
    breakdownCollected: 'Collected from residents',
    breakdownRefunds: 'Refunds to residents',
    breakdownChargebacks: 'Chargebacks',
    breakdownProcessing: 'Card processing',
    breakdownChargebackFees: 'Chargeback fees',
    breakdownSwiftlaneFee: 'Swiftlane fee',
    breakdownClerkage: 'Cancellation fees kept',
    breakdownShare: 'Your share',
    breakdownPaid: 'Paid to you',
    breakdownOwed: 'Owed to you',
    // The `~` note beside a fee row when Stripe's fee for some rows hasn't been read yet. A count,
    // never an estimated amount — an unknown fee is not a zero and not a guess.
    breakdownUnmeasuredCaptures: (n: number): string => `${n} ${n === 1 ? 'capture' : 'captures'} not yet measured`,
    breakdownUnmeasuredDisputes: (n: number): string => `${n} ${n === 1 ? 'dispute' : 'disputes'} not yet measured`,
    breakdownUnmeasuredTooltip:
        'Recent charges whose Stripe fee hasn’t been read yet. The figure updates once Stripe reports each fee — it is never estimated.',
    breakdownUnmeasuredDisputesTooltip:
        'Recent disputes whose Stripe chargeback fee hasn’t been read yet. The figure updates once Stripe reports each fee — it is never estimated.',
    // Only ever shown when the payload actually carries the configured rate — never invented.
    breakdownBpsConfigured: (bps: number): string => `${bps} bps configured`,

    // ----- Accounts & money tab: per-property table -----
    moneyColumnProperty: 'Property',
    // Per-site NET (the site's share) — same word as the KPI tile and the breakdown's subtotal, so it
    // can't be confused with the breakdown's "Collected from residents" (the gross).
    moneyColumnCollected: 'Your share',
    moneyColumnPaid: 'Paid to you',
    moneyColumnOwed: 'Owed to you',
    moneyColumnLastPayment: 'Last payment',
    moneyActivityHeading: 'Activity',
    moneyNoActivity: 'No activity yet.',
    moneyEmpty: 'No properties are collecting through Swiftlane yet. Set one up on the Payment setup tab.',
    moneyLoadError: 'Could not load your earnings.',

    // The money for direct-payout properties isn't here — it's in the property's own Stripe.
    directNotShownNote: 'Properties you collect for directly aren’t shown here — that money goes straight to your own Stripe account.',
    viewInStripe: 'View in Stripe',

    // ----- Accounts & money tab: lazy per-property activity (perf work, 2026-09) -----
    // The activity list itself is unchanged (moneyActivityHeading / moneyNoActivity above); this is
    // just the new failure state for its own lazy, per-site fetch.
    moneyActivityLoadError: 'Could not load activity for this property.',

    // ----- Accounts & money tab: search + filter chips (perf work, 2026-09) -----
    moneySearchPlaceholder: 'Search properties',
    moneySearchClear: 'Clear search',
    moneyNoMatch: 'No sites match.',
    // Chip labels. Distinct keys from the setup tab's own status/summary copy (statusActive,
    // summaryNeedsSetup, …) even where the words are close — these describe a MONEY-tab filter, not
    // a setup-tab status, and the two screens' copy should be free to diverge later.
    moneyChipOwed: 'Owed',
    moneyChipPaidUp: 'Paid up',
    moneyChipNoActivity: 'No activity',
    moneyChipConnected: 'Connected',
    moneyChipNotSetUp: 'Not set up',
    moneyChipNeedsAttention: 'Needs attention',

    // ===== "Get paid by bank transfer" card — TRANSFER_SPEC §6.4 (copy verbatim) =====
    tryAgain: 'Try again',
    recipientCardTitle: 'Get paid by bank transfer',
    recipientCardBody:
        'Swiftlane collects your amenity payments and pays you the balance. Connect a bank account through Stripe to receive payouts directly — Swiftlane never sees your bank details.',
    recipientTagNotSetUp: 'Not set up',
    recipientTagPending: 'Pending',
    recipientTagActive: 'Active',
    recipientSetUpAction: 'Set up with Stripe',
    recipientPendingText: 'Stripe needs a few more details',
    recipientContinueAction: 'Continue setup',
    recipientFinishAction: 'Finish Stripe setup',
    // A Standard (OAuth) account: payouts are the property's own Stripe setting — no link we can mint.
    recipientOauthPendingText:
        'Payouts for this account are managed in your own Stripe dashboard — add a bank account there and Stripe will confirm it.',
    recipientActiveText: (date: string): string => `Payouts go to your bank via Stripe · connected ${date}`,
    recipientActiveTextNoDate: 'Payouts go to your bank via Stripe',
    // Stripe return page after recipient onboarding (kind=RECIPIENT).
    returnRecipientReceivedTitle: 'Bank setup received. Stripe will confirm shortly.',
    returnRecipientReceivedBody:
        'Once Stripe confirms your bank account, Swiftlane can pay your amenity balance straight to it. You can close this page — the status updates on its own.',
    returnRecipientDoneTitle: 'Bank transfers are set up',
    returnRecipientDoneBody: 'Swiftlane can now pay your amenity balance straight to your bank via Stripe.',
};
